/**
 * Energy Meter Calculations Utility
 *
 * Provides comprehensive calculations and formatting for Energy Meter devices.
 * Features:
 * - Power consumption tracking (Watts, kW)
 * - Energy usage calculation (kWh)
 * - Cost calculation based on tariff rates
 * - Peak demand monitoring
 * - Usage pattern analysis (24-hour breakdown)
 * - Historical statistics and trends
 * - Power factor monitoring
 * - Multi-language support (Arabic/English)
 *
 * @version 2.4.0
 * @date January 23, 2026
 */

// ==================== Type Definitions ====================

/**
 * Energy consumption event from the device
 */
export interface EnergyEvent {
  timestamp: Date;
  powerW: number; // Instantaneous power in Watts
  energyKwh?: number; // Cumulative energy in kWh
  voltage?: number; // Voltage in V
  current?: number; // Current in A
  powerFactor?: number; // Power factor (0-1)
  frequency?: number; // Frequency in Hz
}

/**
 * Energy consumption data for a specific hour
 */
export interface EnergyHourPattern {
  hour: number; // Hour of day (0-23)
  energyKwh: number; // Energy consumed in this hour
  averagePowerW: number; // Average power during this hour
  peakPowerW: number; // Peak power during this hour
  cost: number; // Cost for this hour
  isPeakHour: boolean; // True if this is peak consumption hour
}

/**
 * Timeline item for energy events
 */
export interface EnergyTimelineItem {
  timestamp: Date;
  powerW: number;
  energyKwh: number;
  description: string;
  type: 'normal' | 'peak' | 'high' | 'low';
}

/**
 * Comprehensive energy statistics
 */
export interface EnergyStats {
  totalEnergyKwh: number; // Total energy consumed
  averagePowerW: number; // Average power consumption
  peakPowerW: number; // Peak power demand
  peakPowerTime?: Date; // When peak occurred
  totalCost: number; // Total cost
  costPerDay: number; // Average cost per day
  peakHour: number; // Hour with highest consumption
  lowestHour: number; // Hour with lowest consumption
  powerFactorAverage: number; // Average power factor
  efficiencyRating: string; // Efficiency assessment
}

/**
 * Real-time energy meter status
 */
export interface EnergyStatus {
  currentPowerW: number; // Current power consumption
  currentPowerKw: number; // Current power in kW
  todayEnergyKwh: number; // Today's total energy
  todayCost: number; // Today's cost
  yesterdayEnergyKwh: number; // Yesterday's energy
  yesterdayCost: number; // Yesterday's cost
  weekEnergyKwh: number; // This week's energy
  weekCost: number; // This week's cost
  monthEnergyKwh: number; // This month's energy
  monthCost: number; // This month's cost
  peakDemandW: number; // Peak demand today
  voltage?: number; // Current voltage
  current?: number; // Current amperage
  powerFactor?: number; // Current power factor
  frequency?: number; // Current frequency
  status: 'active' | 'idle' | 'offline';
  efficiencyLevel: 'excellent' | 'good' | 'fair' | 'poor';
}

/**
 * Tariff rate structure (price per kWh)
 */
export interface TariffRate {
  pricePerKwh: number; // Price in local currency per kWh
  currency: string; // Currency code (e.g., "SAR", "USD")
  peakMultiplier?: number; // Multiplier for peak hours (default 1)
}

// ==================== Core Calculation Functions ====================

/**
 * Calculate total energy consumption in kWh for a time period
 *
 * @param events - Array of energy events
 * @param startDate - Start of period (optional, defaults to start of array)
 * @param endDate - End of period (optional, defaults to end of array)
 * @returns Total energy consumed in kWh
 */
export function calculateEnergyConsumption(
  events: EnergyEvent[],
  startDate?: Date,
  endDate?: Date,
): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  // Filter events within date range
  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  if (filteredEvents.length === 0) return 0;

  // Method 1: If events have cumulative energyKwh, return difference
  const eventsWithEnergy = filteredEvents.filter((e) => e.energyKwh !== undefined);
  if (eventsWithEnergy.length >= 2) {
    const firstEvent = eventsWithEnergy[0];
    const lastEvent = eventsWithEnergy[eventsWithEnergy.length - 1];
    return Math.max(0, (lastEvent.energyKwh || 0) - (firstEvent.energyKwh || 0));
  }

  // Method 2: Calculate from power readings (integrate power over time)
  let totalEnergyKwh = 0;
  for (let i = 1; i < filteredEvents.length; i++) {
    const prevEvent = filteredEvents[i - 1];
    const currentEvent = filteredEvents[i];

    const timeDiffHours =
      (currentEvent.timestamp.getTime() - prevEvent.timestamp.getTime()) / (1000 * 60 * 60);
    const averagePowerKw = (prevEvent.powerW + currentEvent.powerW) / 2000; // Average power in kW

    totalEnergyKwh += averagePowerKw * timeDiffHours;
  }

  return Math.round(totalEnergyKwh * 1000) / 1000; // Round to 3 decimal places
}

/**
 * Calculate energy cost based on consumption and tariff rate
 *
 * @param energyKwh - Energy consumed in kWh
 * @param tariffRate - Tariff rate structure
 * @returns Cost in local currency
 */
export function calculateEnergyCost(energyKwh: number, tariffRate: TariffRate): number {
  if (energyKwh <= 0) return 0;

  const cost = energyKwh * tariffRate.pricePerKwh;
  return Math.round(cost * 100) / 100; // Round to 2 decimal places
}

/**
 * Find peak power demand in the time period
 *
 * @param events - Array of energy events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Object with peak power and timestamp
 */
export function findPeakDemand(
  events: EnergyEvent[],
  startDate?: Date,
  endDate?: Date,
): { peakPowerW: number; timestamp?: Date } {
  if (!events || events.length === 0) {
    return { peakPowerW: 0 };
  }

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  if (filteredEvents.length === 0) {
    return { peakPowerW: 0 };
  }

  const peakEvent = filteredEvents.reduce((max, event) =>
    event.powerW > max.powerW ? event : max,
  );

  return {
    peakPowerW: peakEvent.powerW,
    timestamp: peakEvent.timestamp,
  };
}

/**
 * Generate 24-hour energy consumption pattern
 *
 * @param events - Array of energy events
 * @param date - Specific date to analyze (optional, defaults to today)
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Array of 24 hourly patterns
 */
export function getEnergyPattern(
  events: EnergyEvent[],
  date: Date = new Date(),
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): EnergyHourPattern[] {
  const patterns: EnergyHourPattern[] = [];

  // Initialize all 24 hours
  for (let hour = 0; hour < 24; hour++) {
    patterns.push({
      hour,
      energyKwh: 0,
      averagePowerW: 0,
      peakPowerW: 0,
      cost: 0,
      isPeakHour: false,
    });
  }

  if (!events || events.length === 0) return patterns;

  // Set date to start of day
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  // Group events by hour
  const eventsByHour: { [hour: number]: EnergyEvent[] } = {};
  events.forEach((event) => {
    const eventDate = new Date(event.timestamp);
    const eventDateOnly = new Date(eventDate);
    eventDateOnly.setHours(0, 0, 0, 0);

    // Only include events from target date
    if (eventDateOnly.getTime() === targetDate.getTime()) {
      const hour = eventDate.getHours();
      if (!eventsByHour[hour]) {
        eventsByHour[hour] = [];
      }
      eventsByHour[hour].push(event);
    }
  });

  // Calculate pattern for each hour
  Object.keys(eventsByHour).forEach((hourStr) => {
    const hour = parseInt(hourStr);
    const hourEvents = eventsByHour[hour];

    if (hourEvents.length > 0) {
      // Calculate energy for this hour
      const hourStart = new Date(targetDate);
      hourStart.setHours(hour, 0, 0, 0);
      const hourEnd = new Date(targetDate);
      hourEnd.setHours(hour, 59, 59, 999);

      const energyKwh = calculateEnergyConsumption(events, hourStart, hourEnd);

      // Calculate average and peak power
      const powerValues = hourEvents.map((e) => e.powerW);
      const averagePowerW = powerValues.reduce((sum, p) => sum + p, 0) / powerValues.length;
      const peakPowerW = Math.max(...powerValues);

      // Calculate cost
      const cost = calculateEnergyCost(energyKwh, tariffRate);

      patterns[hour] = {
        hour,
        energyKwh,
        averagePowerW: Math.round(averagePowerW),
        peakPowerW: Math.round(peakPowerW),
        cost,
        isPeakHour: false,
      };
    }
  });

  // Identify peak hour (highest consumption)
  const maxEnergy = Math.max(...patterns.map((p) => p.energyKwh));
  if (maxEnergy > 0) {
    const peakHourPattern = patterns.find((p) => p.energyKwh === maxEnergy);
    if (peakHourPattern) {
      peakHourPattern.isPeakHour = true;
    }
  }

  return patterns;
}

/**
 * Generate timeline of recent energy events
 *
 * @param events - Array of energy events
 * @param maxItems - Maximum number of items to return
 * @returns Array of timeline items
 */
export function getEnergyTimeline(
  events: EnergyEvent[],
  maxItems: number = 10,
): EnergyTimelineItem[] {
  if (!events || events.length === 0) return [];

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  // Take only the most recent events
  const recentEvents = sortedEvents.slice(0, maxItems);

  // Convert to timeline items
  return recentEvents.map((event) => {
    const powerKw = event.powerW / 1000;

    // Determine type based on power level
    let type: 'normal' | 'peak' | 'high' | 'low' = 'normal';
    if (event.powerW > 3000) type = 'peak';
    else if (event.powerW > 2000) type = 'high';
    else if (event.powerW < 500) type = 'low';

    // Generate description
    let description = `${powerKw.toFixed(2)} kW`;
    if (event.powerFactor) {
      description += ` • PF: ${event.powerFactor.toFixed(2)}`;
    }

    return {
      timestamp: event.timestamp,
      powerW: event.powerW,
      energyKwh: event.energyKwh || 0,
      description,
      type,
    };
  });
}

/**
 * Calculate comprehensive energy statistics
 *
 * @param events - Array of energy events
 * @param startDate - Start of period
 * @param endDate - End of period
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Complete statistics object
 */
export function getEnergyStatistics(
  events: EnergyEvent[],
  startDate: Date,
  endDate: Date,
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): EnergyStats {
  if (!events || events.length === 0) {
    return {
      totalEnergyKwh: 0,
      averagePowerW: 0,
      peakPowerW: 0,
      totalCost: 0,
      costPerDay: 0,
      peakHour: 0,
      lowestHour: 0,
      powerFactorAverage: 0,
      efficiencyRating: 'N/A',
    };
  }

  // Calculate total energy
  const totalEnergyKwh = calculateEnergyConsumption(events, startDate, endDate);

  // Calculate average power
  const powerValues = events
    .filter((e) => {
      const date = new Date(e.timestamp);
      return date >= startDate && date <= endDate;
    })
    .map((e) => e.powerW);
  const averagePowerW =
    powerValues.length > 0
      ? Math.round(powerValues.reduce((sum, p) => sum + p, 0) / powerValues.length)
      : 0;

  // Find peak power
  const { peakPowerW, timestamp: peakPowerTime } = findPeakDemand(events, startDate, endDate);

  // Calculate total cost
  const totalCost = calculateEnergyCost(totalEnergyKwh, tariffRate);

  // Calculate cost per day
  const daysDiff = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const costPerDay = Math.round((totalCost / daysDiff) * 100) / 100;

  // Get hourly pattern to find peak and lowest hours
  const pattern = getEnergyPattern(events, startDate, tariffRate);
  const peakHourPattern = pattern.reduce((max, curr) =>
    curr.energyKwh > max.energyKwh ? curr : max,
  );
  const lowestHourPattern = pattern.reduce((min, curr) =>
    curr.energyKwh < min.energyKwh && curr.energyKwh > 0 ? curr : min,
  );

  // Calculate average power factor
  const powerFactorValues = events
    .filter((e) => e.powerFactor !== undefined)
    .map((e) => e.powerFactor!);
  const powerFactorAverage =
    powerFactorValues.length > 0
      ? Math.round(
          (powerFactorValues.reduce((sum, pf) => sum + pf, 0) / powerFactorValues.length) * 100,
        ) / 100
      : 0;

  // Determine efficiency rating based on power factor
  let efficiencyRating = 'N/A';
  if (powerFactorAverage >= 0.95) efficiencyRating = 'Excellent';
  else if (powerFactorAverage >= 0.85) efficiencyRating = 'Good';
  else if (powerFactorAverage >= 0.75) efficiencyRating = 'Fair';
  else if (powerFactorAverage > 0) efficiencyRating = 'Poor';

  return {
    totalEnergyKwh: Math.round(totalEnergyKwh * 1000) / 1000,
    averagePowerW,
    peakPowerW: Math.round(peakPowerW),
    peakPowerTime,
    totalCost: Math.round(totalCost * 100) / 100,
    costPerDay,
    peakHour: peakHourPattern.hour,
    lowestHour: lowestHourPattern.hour,
    powerFactorAverage,
    efficiencyRating,
  };
}

/**
 * Get real-time energy meter status
 *
 * @param events - Array of energy events
 * @param currentPowerW - Current power reading in Watts
 * @param voltage - Current voltage (optional)
 * @param current - Current amperage (optional)
 * @param powerFactor - Current power factor (optional)
 * @param frequency - Current frequency (optional)
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Complete energy status
 */
export function getEnergyStatus(
  events: EnergyEvent[],
  currentPowerW: number,
  voltage?: number,
  current?: number,
  powerFactor?: number,
  frequency?: number,
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): EnergyStatus {
  const now = new Date();

  // Today's data
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const todayEnergyKwh = calculateEnergyConsumption(events, todayStart, todayEnd);
  const todayCost = calculateEnergyCost(todayEnergyKwh, tariffRate);
  const { peakPowerW: peakDemandW } = findPeakDemand(events, todayStart, todayEnd);

  // Yesterday's data
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const yesterdayEnd = new Date(todayStart);
  yesterdayEnd.setMilliseconds(-1);
  const yesterdayEnergyKwh = calculateEnergyConsumption(events, yesterdayStart, yesterdayEnd);
  const yesterdayCost = calculateEnergyCost(yesterdayEnergyKwh, tariffRate);

  // This week's data
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekEnergyKwh = calculateEnergyConsumption(events, weekStart, now);
  const weekCost = calculateEnergyCost(weekEnergyKwh, tariffRate);

  // This month's data
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnergyKwh = calculateEnergyConsumption(events, monthStart, now);
  const monthCost = calculateEnergyCost(monthEnergyKwh, tariffRate);

  // Determine status
  let status: 'active' | 'idle' | 'offline' = 'offline';
  if (currentPowerW > 100) status = 'active';
  else if (currentPowerW > 0) status = 'idle';

  // Determine efficiency level based on power factor
  let efficiencyLevel: 'excellent' | 'good' | 'fair' | 'poor' = 'fair';
  if (powerFactor !== undefined) {
    if (powerFactor >= 0.95) efficiencyLevel = 'excellent';
    else if (powerFactor >= 0.85) efficiencyLevel = 'good';
    else if (powerFactor >= 0.75) efficiencyLevel = 'fair';
    else efficiencyLevel = 'poor';
  }

  return {
    currentPowerW: Math.round(currentPowerW),
    currentPowerKw: Math.round(currentPowerW / 10) / 100, // Round to 2 decimal places
    todayEnergyKwh: Math.round(todayEnergyKwh * 1000) / 1000,
    todayCost: Math.round(todayCost * 100) / 100,
    yesterdayEnergyKwh: Math.round(yesterdayEnergyKwh * 1000) / 1000,
    yesterdayCost: Math.round(yesterdayCost * 100) / 100,
    weekEnergyKwh: Math.round(weekEnergyKwh * 1000) / 1000,
    weekCost: Math.round(weekCost * 100) / 100,
    monthEnergyKwh: Math.round(monthEnergyKwh * 1000) / 1000,
    monthCost: Math.round(monthCost * 100) / 100,
    peakDemandW: Math.round(peakDemandW),
    voltage,
    current,
    powerFactor,
    frequency,
    status,
    efficiencyLevel,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format power value with unit (W or kW)
 *
 * @param powerW - Power in Watts
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted power string
 */
export function formatPower(powerW: number, lang: 'ar' | 'en' = 'en'): string {
  if (powerW >= 1000) {
    const powerKw = powerW / 1000;
    const formatted =
      lang === 'ar'
        ? powerKw.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : powerKw.toFixed(2);
    return lang === 'ar' ? `${formatted} ك.و` : `${formatted} kW`;
  } else {
    const formatted =
      lang === 'ar'
        ? Math.round(powerW)
            .toString()
            .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : Math.round(powerW).toString();
    return lang === 'ar' ? `${formatted} واط` : `${formatted} W`;
  }
}

/**
 * Format energy value in kWh
 *
 * @param energyKwh - Energy in kWh
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted energy string
 */
export function formatEnergy(energyKwh: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? energyKwh.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : energyKwh.toFixed(2);
  return lang === 'ar' ? `${formatted} ك.و.س` : `${formatted} kWh`;
}

/**
 * Format cost with currency
 *
 * @param cost - Cost value
 * @param currency - Currency code ('SAR', 'USD', etc.)
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted cost string
 */
export function formatCost(
  cost: number,
  currency: string = 'SAR',
  lang: 'ar' | 'en' = 'en',
): string {
  const formatted =
    lang === 'ar'
      ? cost.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : cost.toFixed(2);

  if (currency === 'SAR') {
    return lang === 'ar' ? `${formatted} ريال` : `${formatted} SAR`;
  } else if (currency === 'USD') {
    return lang === 'ar' ? `${formatted} دولار` : `$${formatted}`;
  } else {
    return `${formatted} ${currency}`;
  }
}

/**
 * Format voltage value
 *
 * @param voltage - Voltage in Volts
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted voltage string
 */
export function formatVoltage(voltage: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? Math.round(voltage)
          .toString()
          .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : Math.round(voltage).toString();
  return lang === 'ar' ? `${formatted} فولت` : `${formatted} V`;
}

/**
 * Format current (amperage) value
 *
 * @param current - Current in Amperes
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted current string
 */
export function formatCurrent(current: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? current.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : current.toFixed(2);
  return lang === 'ar' ? `${formatted} أمبير` : `${formatted} A`;
}

/**
 * Format power factor value
 *
 * @param powerFactor - Power factor (0-1)
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted power factor string
 */
export function formatPowerFactor(powerFactor: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? powerFactor.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : powerFactor.toFixed(2);
  return formatted;
}

/**
 * Format frequency value
 *
 * @param frequency - Frequency in Hz
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted frequency string
 */
export function formatFrequency(frequency: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? frequency.toFixed(1).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : frequency.toFixed(1);
  return lang === 'ar' ? `${formatted} هرتز` : `${formatted} Hz`;
}

/**
 * Get efficiency rating text
 *
 * @param rating - Efficiency rating
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized rating text
 */
export function getEfficiencyText(
  rating: 'excellent' | 'good' | 'fair' | 'poor' | 'N/A',
  lang: 'ar' | 'en' = 'en',
): string {
  const translations: { [key: string]: { ar: string; en: string } } = {
    excellent: { ar: 'ممتاز', en: 'Excellent' },
    good: { ar: 'جيد', en: 'Good' },
    fair: { ar: 'مقبول', en: 'Fair' },
    poor: { ar: 'ضعيف', en: 'Poor' },
    'N/A': { ar: 'غير متوفر', en: 'N/A' },
  };

  return translations[rating]?.[lang] || rating;
}

/**
 * Get energy status text
 *
 * @param status - Energy status
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized status text
 */
export function getEnergyStatusText(
  status: 'active' | 'idle' | 'offline',
  lang: 'ar' | 'en' = 'en',
): string {
  const translations: { [key: string]: { ar: string; en: string } } = {
    active: { ar: 'نشط', en: 'Active' },
    idle: { ar: 'خامل', en: 'Idle' },
    offline: { ar: 'غير متصل', en: 'Offline' },
  };

  return translations[status]?.[lang] || status;
}
